import rollbar from '#services/rollbar';
import { useUIStore } from '#store';

/**
 * Evidence-gathering instrumentation for the checkout / payment flow.
 *
 * Users report the payment step getting stuck on "Processing your payment..."
 * with no corresponding charge on Stripe. These logs let us reconstruct, per
 * session in Rollbar, exactly which branch a stuck user hit:
 *   - Stripe.js / PaymentElement never finished loading (scenario 1)
 *   - Pay clicked before Stripe was ready, submit bailed out (scenario 2)
 *   - stripe.confirmPayment() started but never settled (scenario 3)
 *
 * This is read-only telemetry — it must not change checkout behavior.
 */

function baseContext() {
  try {
    const s = useUIStore.getState();
    const sp = s.stripePayments || {};
    return {
      sessionId: s.sessionInfo?.id,
      userId: s.user?._id,
      userEmail: s.user?.email || s.email,
      checkoutStage: s.checkoutStage,
      planSlug: sp.planSlug || s.planSlug,
      subId: sp.subId,
      paymentId: sp.paymentId,
      clientSecretExists: !!sp.clientSecret,
      pathname:
        typeof window !== 'undefined' ? window.location.pathname : undefined,
    };
  } catch (e) {
    return { telemetryContextError: e?.message };
  }
}

/**
 * @param {'debug'|'info'|'warning'|'error'|'critical'} level
 * @param {string} message  short event name; auto-prefixed with "checkout: "
 * @param {object} [extra]  event-specific fields, merged over the base context
 */
export function logCheckout(level, message, extra = {}) {
  const payload = { ...baseContext(), ...extra, ts: new Date().toISOString() };
  const prefixed = `checkout: ${message}`;
  const fn = typeof rollbar[level] === 'function' ? rollbar[level] : rollbar.info;
  try {
    fn.call(rollbar, prefixed, payload);
  } catch (e) {
    // Never let telemetry throw into the payment flow.
  }
}
